import React, { useEffect, useRef, useState } from "react";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import "./GrapesTemplateBuilder.css";
import { useHistory, useParams } from "react-router-dom";
import grapesTemplateApi from "../../services/grapesTemplateApi";

const TEMPLATE_TYPES = ["Email", "Invoice", "Quote", "PurchaseOrder", "Stock", "Checklist", "Other"];
const asArray = (value) =>
  Array.isArray(value)
    ? value
    : value?.events || value?.items || value?.results || value?.data || [];

const getEventId = (event) => event?.eventId || event?.id;
const getEventLabel = (event) =>
  event?.eventName || event?.name || event?.displayName || event?.eventKey || "Unnamed event";
const defaultEmailHtml = `
  <div class="notification-email-shell">
    <div class="notification-email-card">
      <div class="notification-email-brand">
        <img src="/logo-small.png" alt="Spire Essentials" class="notification-email-logo" />
        <span class="notification-email-brand-name">spire<span>essentials</span></span>
      </div>
      <p class="notification-email-greeting">Hello {{user.firstName}},</p>
      <p class="notification-email-intro">{{notification.summary}}</p>
      <div class="notification-email-message">
        <div class="notification-email-sender">
          {{#if notification.senderProfileImage}}<img src="{{notification.senderProfileImage}}" alt="{{notification.senderName}}" class="notification-email-avatar notification-email-avatar-image" />{{else}}<span class="notification-email-avatar">{{notification.senderInitials}}</span>{{/if}}
          <span><strong>{{notification.senderName}}</strong> sent a new message.</span>
        </div>
        <a class="notification-email-action" href="{{notification.actionUrl}}">{{notification.actionLabel}}</a>
      </div>
      <div class="notification-email-rule"></div>
      <p class="notification-email-description">Spire Essentials is a collaboration software platform for teams that helps you to align projects with all people involved. <a href="{{company.learnMoreUrl}}">Learn more</a></p>
      <div class="notification-email-footer">
        <span>Spire Academy</span><span class="notification-email-divider">|</span><a href="{{company.policiesUrl}}">Policies</a>
        <span class="notification-email-social"><a href="{{company.xUrl}}">X</a><a href="{{company.linkedinUrl}}">in</a></span>
      </div>
    </div>
    <p class="notification-email-notice">You are receiving this email because notification email delivery is enabled for your account.</p>
  </div>
`;
const defaultEmailCss = `
  * { box-sizing: border-box; }
  body { margin: 0; background: #f7f7f7; color: #29313d; font-family: Arial, Helvetica, sans-serif; }
  .notification-email-shell { width: 100%; min-height: 680px; padding: 48px 20px 30px; background: #f7f7f7; }
  .notification-email-card { width: 450px; max-width: 100%; margin: 0 auto; padding: 22px 21px 20px; background: #fff; border: 1px solid #dfe3e8; border-radius: 16px; }
  .notification-email-brand { display: flex; align-items: center; gap: 6px; margin-bottom: 24px; }
  .notification-email-logo { width: 26px; height: 32px; object-fit: contain; }
  .notification-email-brand-name { color: #56606b; font-size: 15px; letter-spacing: -0.4px; }
  .notification-email-brand-name span { color: #1e2832; font-weight: 700; }
  .notification-email-greeting { margin: 0 0 16px; font-size: 12px; line-height: 1.6; }
  .notification-email-intro { margin: 0 0 20px; color: #606873; font-size: 11px; line-height: 1.6; }
  .notification-email-message { padding: 14px; border: 1px solid #dfe3e8; border-radius: 12px; background: #fff; }
  .notification-email-sender { display: flex; align-items: center; gap: 8px; color: #343c47; font-size: 11px; line-height: 1.5; }
  .notification-email-avatar { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: #eaf0ff; color: #0b357e; font-size: 8px; font-weight: 700; }
  .notification-email-avatar-image { display: inline-block; object-fit: cover; }
  .notification-email-action { display: inline-block; margin-top: 14px; padding: 10px 16px; border-radius: 7px; background: #07347d; color: #fff; font-size: 12px; font-weight: 700; text-decoration: none; }
  .notification-email-rule { height: 1px; margin: 18px 0 14px; background: #dfe3e8; }
  .notification-email-description { margin: 0; color: #657080; font-size: 10px; line-height: 1.65; }
  .notification-email-description a { color: #07347d; font-weight: 700; text-decoration: none; }
  .notification-email-footer { display: flex; align-items: center; gap: 12px; margin-top: 20px; color: #657080; font-size: 10px; }
  .notification-email-footer a { color: #657080; text-decoration: none; }
  .notification-email-divider { color: #aab0b8; }
  .notification-email-social { display: flex; gap: 14px; margin-left: auto; font-size: 17px; font-weight: 700; }
  .notification-email-social a { color: #343a40; }
  .notification-email-notice { margin: 12px auto 0; color: #8da6c8; font-size: 9px; text-align: center; }
  @media (max-width: 520px) { .notification-email-shell { padding: 24px 12px; } .notification-email-card { padding: 18px 16px; } }
`;
const defaultProject = { pages: [{ name: "Main", component: defaultEmailHtml }] };
const getAvailableFields = (response) => {
  const fields = Array.isArray(response) ? response : [];

  return fields
    .map((field) => {
      const token = field?.fieldPlaceHolder || field?.fieldPlaceholder || field?.placeholder || field?.token || field?.fieldName;
      const label = field?.fieldName || field?.name || field?.displayName || token;
      return token
        ? {
            token: String(token),
            label: String(label),
            fieldType: field?.fieldType || "text",
            isRepeatable: Boolean(field?.isRepeatable),
            collectionPath: field?.collectionPath || "",
            format: field?.format || "",
            description: field?.description || "",
          }
        : null;
    })
    .filter((field, index, list) => field && list.findIndex((item) => item.token === field.token) === index);
};

const fieldBlockId = (token, index) =>
  `spire-field-${index}-${token.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

const tableDataSources = (templateType, fields) => {
  const knownSource = {
    Invoice: "invoice.items",
    Quote: "quote.items",
    PurchaseOrder: "purchaseOrder.items",
    Stock: "stock.items",
    Other: "proforma.items",
  }[templateType];
  const fieldSources = fields
    .filter((field) => field.isRepeatable && field.collectionPath)
    .map((field) => field.collectionPath);
  return Array.from(new Set([knownSource, ...fieldSources].filter(Boolean)));
};

const extractTableMetadata = (html) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(html || "", "text/html");
  return Array.from(document.querySelectorAll("table[data-spire-table]")).map((table, index) => ({
    id: table.getAttribute("data-table-id") || `table-${index + 1}`,
    dataSource: table.getAttribute("data-source") || "",
    columns: Array.from(table.querySelectorAll("tbody tr:first-child td[data-spire-cell]")).map((cell, columnIndex) => ({
      id: cell.getAttribute("data-column-id") || `column-${columnIndex + 1}`,
      field: cell.getAttribute("data-field") || "",
      header: table.querySelectorAll("thead th")[columnIndex]?.textContent?.trim() || `Column ${columnIndex + 1}`,
      format: cell.getAttribute("data-format") || "text",
    })),
  }));
};

const prepareImportedMarkup = (html, css) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(html || "", "text/html");
  const embeddedStyles = Array.from(document.querySelectorAll("style"))
    .map((style) => style.textContent || "")
    .join("\n");

  document.querySelectorAll("script, iframe, object, embed").forEach((element) => element.remove());
  document.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (/^on/i.test(attribute.name) || /javascript:/i.test(attribute.value)) {
        element.removeAttribute(attribute.name);
      }
    });
  });
  document.querySelectorAll("style").forEach((style) => style.remove());

  return {
    html: document.body.innerHTML,
    css: [embeddedStyles, css || ""].filter(Boolean).join("\n"),
  };
};

const extractTemplateTokens = (html) =>
  Array.from(new Set((html.match(/{{\s*[^{}]+?\s*}}|{[A-Za-z0-9_.-]+}/g) || []).map((token) => token.trim())));

const GrapesTemplateBuilder = () => {
  const history = useHistory();
  const { id: routeId } = useParams();
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);
  const queryType = new URLSearchParams(window.location.search).get("type");
  const [templateType, setTemplateType] = useState("Email");
  const [eventId, setEventId] = useState("");
  const [notificationEvents, setNotificationEvents] = useState([]);
  const [eventsStatus, setEventsStatus] = useState("idle");
  const [templateName, setTemplateName] = useState("New template");
  const [subjectTemplate, setSubjectTemplate] = useState("{{notification.title}}");
  const [templateId, setTemplateId] = useState(routeId || null);
  const [status, setStatus] = useState("Not saved");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [fieldsStatus, setFieldsStatus] = useState("loading");
  const [showImport, setShowImport] = useState(false);
  const [importHtml, setImportHtml] = useState("");
  const [importCss, setImportCss] = useState("");
  const [importTokens, setImportTokens] = useState([]);
  const [importError, setImportError] = useState("");
  const fieldBlockIdsRef = useRef([]);

  const addAvailableFieldBlocks = (editor, fields) => {
    fieldBlockIdsRef.current.forEach((id) => editor.BlockManager.remove(id));
    fieldBlockIdsRef.current = [];

    fields.forEach(({ token, label, fieldType, isRepeatable, collectionPath, format, description }, index) => {
      const id = fieldBlockId(token, index);
      const category = isRepeatable ? "Repeating fields" : "Spire fields";
      editor.BlockManager.add(id, {
        label,
        category,
        attributes: { title: description || token },
        content: {
          type: "text",
          content: token,
          attributes: {
            class: "spire-template-field",
            "data-template-field": token,
            "data-field-type": fieldType,
            "data-repeatable": isRepeatable ? "true" : "false",
            "data-collection-path": collectionPath,
            "data-format": format,
          },
        },
      });
      fieldBlockIdsRef.current.push(id);
    });
    editor.BlockManager.getCategories().forEach((category) => {
      if (["Spire fields", "Repeating fields"].includes(category.id)) {
        category.set("open", true);
      }
    });
  };

  const registerDynamicTable = (editor, fields) => {
    const sources = tableDataSources(templateType, fields);
    editor.DomComponents.addType("spire-table", {
      isComponent: (element) =>
        element?.tagName === "TABLE" && element.hasAttribute("data-spire-table"),
      model: {
        defaults: {
          tagName: "table",
          droppable: "[data-spire-cell]",
          attributes: {
            "data-spire-table": "true",
            "data-table-id": `table-${Date.now()}`,
            "data-source": sources[0] || "items",
            class: "spire-dynamic-table",
          },
          traits: [
            {
              type: "select",
              name: "data-source",
              label: "Data source",
              options: sources.map((source) => ({ id: source, label: source })),
            },
          ],
        },
      },
    });
  };

  useEffect(() => {
    if (queryType && TEMPLATE_TYPES.includes(queryType)) setTemplateType(queryType);
  }, [queryType]);

  useEffect(() => {
    if (templateType !== "Email") return undefined;
    let active = true;
    setEventsStatus("loading");
    grapesTemplateApi
      .notificationEvents()
      .then((response) => {
        if (!active) return;
        setNotificationEvents(asArray(response));
        setEventsStatus("loaded");
      })
      .catch(() => active && setEventsStatus("error"));
    return () => {
      active = false;
    };
  }, [templateType]);

  useEffect(() => {
    if (!editorContainerRef.current) return undefined;
    const editor = grapesjs.init({
      container: editorContainerRef.current,
      height: "100%",
      noticeOnUnload: true,
      storageManager: false,
      selectorManager: { componentFirst: true },
      blockManager: { blocks: [
        { id: "section", label: "Section", category: "Basic", content: "<section style=\"padding:24px\"><h2>Section title</h2><p>Section content</p></section>" },
        { id: "text", label: "Text", category: "Basic", content: "<p>Write your content here</p>" },
        { id: "button", label: "Button", category: "Basic", content: "<a href=\"#\" style=\"display:inline-block;padding:12px 20px;background:#173b73;color:#fff;text-decoration:none\">Open link</a>" },
        { id: "company-logo", label: "Company logo", category: "Spire", content: "<img src=\"{{company.logo}}\" alt=\"{{company.name}}\" style=\"max-width:180px;max-height:60px;object-fit:contain\" />" },
        { id: "token", label: "Dynamic token", category: "Spire", content: "<span data-token=\"user.firstName\">{{user.firstName}}</span>" },
        { id: "spire-table", label: "Dynamic table", category: "Spire tables", content: `<table data-spire-table=\"true\" data-table-id=\"table-${Date.now()}\" data-source=\"items\" class=\"spire-dynamic-table\"><thead><tr><th data-spire-header>Item</th><th data-spire-header>Quantity</th><th data-spire-header>Amount</th></tr></thead><tbody><tr data-spire-row-template><td data-spire-cell data-column-id=\"column-1\" data-field=\"item.description\">{{item.description}}</td><td data-spire-cell data-column-id=\"column-2\" data-field=\"item.quantity\">{{item.quantity}}</td><td data-spire-cell data-column-id=\"column-3\" data-field=\"item.amount\">{{item.amount}}</td></tr></tbody></table>` },
      ] },
      deviceManager: { devices: [{ id: "Desktop", width: "" }, { id: "Mobile", width: "375px" }] },
    });
    registerDynamicTable(editor, availableFields);
    editorRef.current = editor;
    if (!templateId) {
      editor.loadProjectData(defaultProject);
      editor.setStyle(defaultEmailCss);
    }
    editor.on("change", () => setStatus("Unsaved changes"));
    editor.on("component:add", (component) => {
      const attributes = component.getAttributes?.() || {};
      const field = attributes["data-template-field"];
      if (!field) return;

      let parent = component.parent?.();
      while (parent) {
        const parentAttributes = parent.getAttributes?.() || {};
        if (parentAttributes["data-spire-cell"] !== undefined) {
          parent.addAttributes({
            "data-field": field,
            "data-format": attributes["data-format"] || "text",
          });
          break;
        }
        parent = parent.parent?.();
      }
    });
    addAvailableFieldBlocks(editor, availableFields);
    return () => { editor.destroy(); editorRef.current = null; };
  }, []);

  useEffect(() => {
    let active = true;
    setFieldsStatus("loading");

    grapesTemplateApi.availableFields(templateType, eventId || undefined).then((response) => {
      if (!active) return;
      const fields = getAvailableFields(response);
      setAvailableFields(fields);
      setFieldsStatus("loaded");
      if (editorRef.current) addAvailableFieldBlocks(editorRef.current, fields);
    }).catch(() => {
      if (active) setFieldsStatus("error");
    });

    return () => { active = false; };
  }, [templateType, eventId]);

  useEffect(() => {
    if (!templateId || !editorRef.current) return undefined;
    let active = true;
    setBusy(true);
    grapesTemplateApi.get(templateId).then((result) => {
      const version = result?.version;
      const template = result?.template;
      if (!active || !version) return;
      setTemplateName(template?.templateName || "New template");
      setTemplateType(template?.templateType || "Email");
      setEventId(template?.eventId || "");
      setSubjectTemplate(template?.subjectTemplate || "{{notification.title}}");
      editorRef.current.loadProjectData(JSON.parse(version.projectJson));
      setStatus("Saved");
    }).catch((error) => active && setStatus(error.message || "Unable to load template")).finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [templateId]);

  const buildPayload = () => {
    const editor = editorRef.current;
    const compiledHtml = editor.getHtml();
    return {
      templateId: templateId || null,
      templateType,
      templateName,
      eventId: templateType === "Email" ? eventId || null : null,
      outputType: templateType === "Email" ? "HTML" : "PDF",
      subjectTemplate,
      projectJson: JSON.stringify(editor.getProjectData()),
      compiledHtml,
      compiledCss: editor.getCss(),
      tablesJson: JSON.stringify(extractTableMetadata(compiledHtml)),
    };
  };

  const saveDraft = async () => {
    if (!editorRef.current) return;
    if (templateType === "Email" && !eventId) {
      setStatus("Choose an event before saving this notification email.");
      return;
    }
    setBusy(true); setStatus("Saving...");
    try {
      const payload = buildPayload();
      const result = await grapesTemplateApi.saveDraft(templateId, payload);
      const saved = result?.template;
      if (saved?.id && saved.id !== templateId) {
        setTemplateId(saved.id);
        history.replace(`/grapes-template-builder/${saved.id}`);
      }
      setStatus("Saved");
    } catch (error) { setStatus(error.message || "Save failed"); } finally { setBusy(false); }
  };

  const openPreview = async () => {
    if (!editorRef.current) return;
    setBusy(true);
    try {
      const result = await grapesTemplateApi.preview({
        ...buildPayload(),
        sampleData: {
          "user.firstName": "Atul",
          "notification.title": "Task assigned",
          "notification.summary": "sent a new message.",
          "notification.senderInitials": "RT",
          "notification.senderProfileImage": "",
          "notification.senderName": "Ritika Tyagi",
          "notification.actionUrl": "#",
          "notification.actionLabel": "Open chat",
          "company.learnMoreUrl": "#",
          "company.logo": "https://s3.spire-solutions-1.res.trans-ix.whitesky.cloud/spiresolutions-uat/SpireEssentials%20Logo.png",
          "COMPANY_LOGO": "https://s3.spire-solutions-1.res.trans-ix.whitesky.cloud/spiresolutions-uat/SpireEssentials%20Logo.png",
          "company.policiesUrl": "#",
          "company.xUrl": "#",
          "company.linkedinUrl": "#",
          items: [
            { description: "Design services", quantity: 2, amount: 250 },
            { description: "Implementation services", quantity: 1, amount: 750 },
          ],
        },
      });
      setPreview(result || null);
    } catch (error) { setStatus(error.message || "Preview failed"); } finally { setBusy(false); }
  };

  const publish = async () => {
    if (templateType === "Email" && !eventId) {
      setStatus("Choose an event before publishing this notification email.");
      return;
    }
    if (!templateId) return saveDraft();
    setBusy(true);
    try { await grapesTemplateApi.publish(templateId); setStatus("Published"); } catch (error) { setStatus(error.message || "Publish failed"); } finally { setBusy(false); }
  };

  const setDefault = async () => {
    if (!templateId) return;
    setBusy(true);
    try { await grapesTemplateApi.setDefault(templateId); setStatus("Default template"); } catch (error) { setStatus(error.message || "Unable to set default template"); } finally { setBusy(false); }
  };

  const readImportFile = (file, setter) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setter(String(event.target?.result || ""));
    reader.readAsText(file);
  };

  const importTemplate = () => {
    if (!editorRef.current) return;
    if (!importHtml.trim()) {
      setImportError("HTML content is required.");
      return;
    }

    try {
      const prepared = prepareImportedMarkup(importHtml, importCss);
      if (!prepared.html.trim()) {
        setImportError("The imported HTML does not contain any body content.");
        return;
      }
      editorRef.current.setComponents(prepared.html);
      editorRef.current.setStyle(prepared.css);
      editorRef.current.UndoManager.clear();
      setImportTokens(extractTemplateTokens(prepared.html));
      setImportError("");
      setShowImport(false);
      setStatus("Unsaved changes");
    } catch (error) {
      setImportError(error.message || "Unable to import HTML.");
    }
  };

  return <div className="grapes-template-page">
    <div className="grapes-template-toolbar">
      <input value={templateName} onChange={(event) => { setTemplateName(event.target.value); setStatus("Unsaved changes"); }} aria-label="Template name" />
      <select value={templateType} onChange={(event) => setTemplateType(event.target.value)} aria-label="Template type">{TEMPLATE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select>
      {templateType === "Email" && <select value={eventId} onChange={(event) => { setEventId(event.target.value); setStatus("Unsaved changes"); }} aria-label="Email event" disabled={eventsStatus === "loading"}>
        <option value="">{eventsStatus === "loading" ? "Loading events..." : "Choose notification event"}</option>
        {notificationEvents.map((event) => <option key={getEventId(event)} value={getEventId(event)}>{getEventLabel(event)}</option>)}
      </select>}
      {templateType === "Email" && <input className="grapes-template-subject" value={subjectTemplate} onChange={(event) => { setSubjectTemplate(event.target.value); setStatus("Unsaved changes"); }} placeholder="Email subject (supports event fields)" aria-label="Email subject" />}
      <span className="grapes-template-toolbar-spacer" /><span className="grapes-template-status">{status}</span>
      <button className="grapes-template-button grapes-template-button-secondary" disabled={busy} onClick={openPreview}>Preview</button>
      <button className="grapes-template-button grapes-template-button-secondary" disabled={busy} onClick={() => { setImportError(""); setShowImport(true); }}>Import HTML/CSS</button>
      <button className="grapes-template-button grapes-template-button-secondary" disabled={busy} onClick={saveDraft}>Save draft</button>
      <button className="grapes-template-button grapes-template-button-primary" disabled={busy || !templateId} onClick={publish}>Publish</button>
      {["Invoice", "Quote", "PurchaseOrder", "Stock", "Email", "Other"].includes(templateType) && <button className="grapes-template-button grapes-template-button-secondary" disabled={busy || !templateId} onClick={setDefault}>Set as default</button>}
    </div>
    {showImport && <div className="grapes-template-import" role="dialog" aria-modal="true">
      <div className="grapes-template-import-card">
        <div className="grapes-template-import-header">
          <strong>Import HTML/CSS</strong>
          <button className="grapes-template-button grapes-template-button-secondary" onClick={() => setShowImport(false)}>Close</button>
        </div>
        <div className="grapes-template-import-body">
          <div className="grapes-template-import-file-row">
            <label>HTML file <input type="file" accept=".html,.htm,text/html" onChange={(event) => readImportFile(event.target.files?.[0], setImportHtml)} /></label>
            <label>CSS file <input type="file" accept=".css,text/css" onChange={(event) => readImportFile(event.target.files?.[0], setImportCss)} /></label>
          </div>
          <label className="grapes-template-import-label">HTML</label>
          <textarea value={importHtml} onChange={(event) => setImportHtml(event.target.value)} placeholder="Paste HTML here" />
          <label className="grapes-template-import-label">CSS</label>
          <textarea value={importCss} onChange={(event) => setImportCss(event.target.value)} placeholder="Paste CSS here" />
          {importTokens.length > 0 && <div className="grapes-template-import-tokens">Detected fields: {importTokens.join(", ")}</div>}
          {importError && <div className="grapes-template-import-error">{importError}</div>}
          <div className="grapes-template-import-actions">
            <button className="grapes-template-button grapes-template-button-secondary" onClick={() => { setImportHtml(""); setImportCss(""); setImportTokens([]); setImportError(""); }}>Clear</button>
            <button className="grapes-template-button grapes-template-button-primary" onClick={importTemplate}>Load into builder</button>
          </div>
        </div>
      </div>
    </div>}
    {templateType === "Email" && eventsStatus === "error" && <div className="grapes-template-event-error">Unable to load email events. Refresh and try again.</div>}
    <div className="grapes-template-workspace">
      <div ref={editorContainerRef} className="grapes-template-editor" />
      <div className={`grapes-template-fields-status grapes-template-fields-${fieldsStatus}`}>
        {fieldsStatus === "loading" && "Loading Spire fields..."}
        {fieldsStatus === "loaded" && `${availableFields.length} Spire fields available`}
        {fieldsStatus === "error" && "Unable to load Spire fields"}
      </div>
    </div>
    {preview && <div className="grapes-template-preview" role="dialog" aria-modal="true"><div className="grapes-template-preview-card"><div className="grapes-template-preview-header"><strong>Template preview</strong><button className="grapes-template-button grapes-template-button-secondary" onClick={() => setPreview(null)}>Close</button></div><div className="grapes-template-preview-body">{preview.missingVariables?.length > 0 && <div className="grapes-template-preview-warning">Missing values: {preview.missingVariables.join(", ")}</div>}<iframe title="Template preview" className="grapes-template-preview-frame" srcDoc={`<style>${preview.css || ""}</style>${preview.html || ""}`} /></div></div></div>}
  </div>;
};

export default GrapesTemplateBuilder;
